import { createContext, useContext, useState } from 'react';

export interface BackgroundUploadProps {
  topic: any;
  media: any[]; // Array of files
  isUploadStart: boolean;
  totalMediaSize: number;
  totalUploadedMediaSize: number;
  progress: number;
}

export interface IBackgroundUpload {
  backgroundUploadData: BackgroundUploadProps[];
  setBackgroundUploadDetails: (data: any) => void;
  createPart: (action: IActionProps, data?: BackgroundUploadProps[], index?: number) => void;
  updateBackgroundUploadProgress: (action: IActionProps, data?: BackgroundUploadProps[], index?: number) => void;
  unsetBackgroundUploadData: (index?: number) => void;
}

interface IActionProps {
  fileNumber?: number;
  partNumber?: number;
  uploadedSize?: number;
  presignedUrl?: string;
  totalPartSize?: number;
}

export const backgroundUploadContext = createContext<IBackgroundUpload>({} as IBackgroundUpload);

const useProvideBackgroundUpload = () => {
  const [backgroundUploadData, setBackgroundUploadData] = useState<BackgroundUploadProps[]>([]);

  const calculateProgress = (media: any[], totalMediaSize: number) => {
    const totalUploaded = media
      .map(file => file.parts.reduce((sum: number, part: any) => sum + part.uploadedPartSize, 0))
      .reduce((sum: number, fileUploaded: number) => sum + fileUploaded, 0);
    const progress = Math.round((totalUploaded * 100) / totalMediaSize);
    return { totalUploaded, progress };
  };

  const setBackgroundUploadDetails = (data: any) => {
    const newData: BackgroundUploadProps = {
      topic: data?.backgroundTopicDetail?.topic,
      media: data?.backgroundTopicDetail?.media || [],
      isUploadStart: false,
      totalMediaSize: data.totalMediaSize,
      totalUploadedMediaSize: 0,
      progress: 0,
    };
    setBackgroundUploadData(prev => [...prev, newData]);
  };

  const createPart = (action: IActionProps, latestData?: BackgroundUploadProps[], index?: number) => {
    setBackgroundUploadData(prev => {
      const data = prev;
      return data.map((bg, i) => {
        if (i !== index) return bg;

        const updatedMedia = bg.media.map((file: any) => {
          if (file.fileNumber !== action.fileNumber) return file;

          const existingPart = file.parts.find((p: any) => p.partNumber === action.partNumber);
          if (!existingPart) {
            file.parts.push({
              partNumber: action.partNumber,
              presignedUrl: action.presignedUrl,
              totalPartSize: action.totalPartSize,
              uploadedPartSize: 0,
            });
          }
          return file;
        });

        const { totalUploaded, progress } = calculateProgress(updatedMedia, bg.totalMediaSize);
        return { ...bg, media: updatedMedia, totalUploadedMediaSize: totalUploaded, progress, isUploadStart: true };
      });
    });
  };

  const updateBackgroundUploadProgress = (action: IActionProps, latestData?: BackgroundUploadProps[], index?: number) => {
    setBackgroundUploadData(prev => {
      const data = prev;
      return data.map((bg, i) => {
        if (i !== index) return bg;

        const updatedMedia = bg.media.map((file: any) => {
          if (file.fileNumber !== action.fileNumber) return file;

          const partIndex = file.parts.findIndex((p: any) => p.partNumber === action.partNumber);
          if (partIndex > -1) file.parts[partIndex].uploadedPartSize = action.uploadedSize || 0;

          return file;
        });

        const { totalUploaded, progress } = calculateProgress(updatedMedia, bg.totalMediaSize);
        return { ...bg, media: updatedMedia, totalUploadedMediaSize: totalUploaded, progress, isUploadStart: true };
      });
    });
  };

  const unsetBackgroundUploadData = (index?: number) => {
    setBackgroundUploadData(prev =>
      index !== undefined ? prev.filter((_, i) => i !== index) : []
    );
  };

  return {
    backgroundUploadData,
    setBackgroundUploadDetails,
    createPart,
    updateBackgroundUploadProgress,
    unsetBackgroundUploadData,
  };
};

interface Props {
  children: React.ReactNode;
}

export const ProvideBackgroundUpload = ({ children }: Props) => {
  const value = useProvideBackgroundUpload();
  return <backgroundUploadContext.Provider value={value}>{children}</backgroundUploadContext.Provider>;
};

export const useBackgroundUpload = () => useContext(backgroundUploadContext);